import ReactECharts from "echarts-for-react";
import { useComputedColorScheme } from "@mantine/core";
import type { EChartsOption } from "echarts";
import { getChartThemeName } from "../utils/chart-theme";
import { SkeletonChart } from "./SkeletonChart";
import { EmptyState } from "./EmptyState";
import { IconChartBar } from "@tabler/icons-react";
import { useMemo } from "react";

const CHART_OPTS = { renderer: "svg" as const };

interface ChartWrapperProps {
  option: EChartsOption;
  height?: number;
  loading?: boolean;
  empty?: boolean;
  /** Accessible name for the chart — describes what the chart shows. */
  ariaLabel: string;
  onEvents?: Record<string, (params: unknown) => void>;
}

export function ChartWrapper({
  option,
  height = 400,
  loading = false,
  empty = false,
  ariaLabel,
  onEvents,
}: ChartWrapperProps) {
  const colorScheme = useComputedColorScheme("light");
  const theme = getChartThemeName(colorScheme);
  const chartStyle = useMemo(() => ({ height, width: "100%" as const }), [height]);

  if (loading) return <SkeletonChart height={height} />;

  if (empty) {
    return (
      <EmptyState
        icon={IconChartBar}
        title="No chart data"
        description="Adjust your filters or check back when data is available."
      />
    );
  }

  return (
    <div role="img" aria-label={ariaLabel}>
      <ReactECharts
        option={option}
        theme={theme}
        style={chartStyle}
        opts={CHART_OPTS}
        notMerge
        onEvents={onEvents}
      />
    </div>
  );
}
